// A picture's thumbnail, made on the device at upload (docs/areas/files.md):
// at most THUMB_PX on its longer side, a JPEG (every browser encodes it; Safari
// cannot encode WebP), kept as its own object (`thumb_key`), so the grid never
// downloads the full picture.

export const THUMB_PX = 320

/** The size a thumbnail is drawn at: the longer side THUMB_PX, never upscaled. */
export const thumbSize = (w: number, h: number, max = THUMB_PX): { w: number; h: number } => {
  if (w <= 0 || h <= 0) return { w: 0, h: 0 }
  const s = Math.min(1, max / Math.max(w, h))
  return { w: Math.max(1, Math.round(w * s)), h: Math.max(1, Math.round(h * s)) }
}

/** A JPEG thumbnail of a picture, or null when the browser cannot read it (HEIC outside Safari, a broken file). */
export const makeThumbnail = async (file: Blob): Promise<Blob | null> => {
  try {
    const bmp = await createImageBitmap(file)
    const { w, h } = thumbSize(bmp.width, bmp.height)
    if (!w) return null
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.drawImage(bmp, 0, 0, w, h)
    bmp.close()
    return await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.8))
  } catch {
    return null
  }
}
